import { FunctionComponent, useMemo } from 'react';
import FileUploadHeader from './FileUploadHeader';
import { useTranslation } from 'react-i18next';
import { Table } from 'UI';
import { Card, formattedCurrency, Box } from 'vopay';
import { FileUploadProduct, FileUploadService } from '../types';

type FileUploadHeaderProps = {
  product: FileUploadProduct;
  service: FileUploadService;
  fileName: string;
  numberOfTransactions: number;
  amount: number;
  currency: string;
  clientAccountsEnabled: boolean;
  transactionData: {
    [key: string]: string | number | boolean | undefined;
  }[];
};

type FC = FunctionComponent<FileUploadHeaderProps>;

const FileUploadReview: FC = (props: FileUploadHeaderProps) => {
  const { product, service, fileName, numberOfTransactions, amount, currency, transactionData, clientAccountsEnabled } =
    props;
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.Review' });

  const getServiceName = (product: FileUploadProduct, service: FileUploadService): string => {
    if (product === 'ePay') return 'eLinx™ Pay';
    if (product === 'eCollect') return 'eLinx™ Collect';
    if (product === 'eFlex') return 'eLinx™ Flexible Payments';
    return service;
  };

  const getTableProps = () => {
    if (product === 'ePay' || product === 'eCollect' || product === 'eFlex') {
      const baseColumns = [
        { id: 'Id', header: t('ELinxTable.id'), align: 'left' as const, template: (row: any) => row.data.Id },
        {
          id: 'ClientAccountID',
          header: t('ELinxTable.clientAccountID'),
          align: 'left' as const,
          template: (row: any) => row.data.ClientAccountID,
        },
        {
          id: 'SenderName',
          header: t('ELinxTable.senderName'),
          align: 'left' as const,
          template: (row: any) => row.data.SenderName,
        },
        {
          id: 'ReceiverName',
          header: t('ELinxTable.receiverName'),
          align: 'left' as const,
          template: (row: any) => row.data.ReceiverName,
        },
        {
          id: 'ReceiverEmailAddress',
          header: t('ELinxTable.receiverEmailAddress'),
          align: 'left' as const,
          template: (row: any) => row.data.ReceiverEmailAddress,
        },
        {
          id: 'ReceiverPhoneNumber',
          header: t('ELinxTable.receiverPhoneNumber'),
          align: 'left' as const,
          template: (row: any) => row.data.ReceiverPhoneNumber,
        },
        {
          id: 'PaymentMethod',
          header: t('ELinxTable.paymentMethod'),
          align: 'left' as const,
          template: (row: any) => row.data.PaymentMethod,
        },
        {
          id: 'BankingPaymentRail',
          header: t('ELinxTable.bankingPaymentRail'),
          align: 'left' as const,
          template: (row: any) => row.data.BankingPaymentRail,
        },
        {
          id: 'Amount',
          header: t('ELinxTable.amount'),
          align: 'left' as const,
          template: (row: any) => row.data.Amount,
        },
        {
          id: 'Currency',
          header: t('ELinxTable.currency'),
          align: 'left' as const,
          template: (row: any) => row.data.Currency,
        },
        { id: 'Type', header: t('ELinxTable.type'), align: 'left' as const, template: (row: any) => row.data.Type },
        {
          id: 'PaymentType',
          header: t('ELinxTable.paymentType'),
          align: 'left' as const,
          template: (row: any) => row.data.PaymentType,
        },
        {
          id: 'MessageForRecipient',
          header: t('ELinxTable.messageForRecipient'),
          align: 'left' as const,
          template: (row: any) => row.data.MessageForRecipient,
        },
        {
          id: 'PaymentFrequency',
          header: t('ELinxTable.paymentFrequency'),
          align: 'left' as const,
          template: (row: any) => row.data.PaymentFrequency,
        },
        {
          id: 'NameOfFrequency',
          header: t('ELinxTable.nameOfFrequency'),
          align: 'left' as const,
          template: (row: any) => row.data.NameOfFrequency,
        },
        {
          id: 'ScheduleStartDate',
          header: t('ELinxTable.scheduleStartDate'),
          align: 'left' as const,
          template: (row: any) => row.data.ScheduleStartDate,
        },
        {
          id: 'ScheduleEndDate',
          header: t('ELinxTable.scheduleEndDate'),
          align: 'left' as const,
          template: (row: any) => row.data.ScheduleEndDate,
        },
        {
          id: 'SemiMonthlyFrequencyType',
          header: t('ELinxTable.semiMonthlyFrequencyType'),
          align: 'left' as const,
          template: (row: any) => row.data.SemiMonthlyFrequencyType,
        },
        {
          id: 'EndingAfterPayments',
          header: t('ELinxTable.endingAfterPayments'),
          align: 'left' as const,
          template: (row: any) => row.data.EndingAfterPayments,
        },
        {
          id: 'InvoiceNumber',
          header: t('ELinxTable.invoiceNumber'),
          align: 'left' as const,
          template: (row: any) => row.data.InvoiceNumber,
        },
        {
          id: 'DocumentID',
          header: t('ELinxTable.documentID'),
          align: 'left' as const,
          template: (row: any) => row.data.DocumentID,
        },
        {
          id: 'ClientReferenceNumber',
          header: t('ELinxTable.clientReferenceNumber'),
          align: 'left' as const,
          template: (row: any) => row.data.ClientReferenceNumber,
        },
        {
          id: 'TransactionLabel',
          header: t('ELinxTable.transactionLabel'),
          align: 'left' as const,
          template: (row: any) => row.data.TransactionLabel,
        },
        { id: 'Note', header: t('ELinxTable.note'), align: 'left' as const, template: (row: any) => row.data.Note },
        {
          id: 'SendNotification',
          header: t('ELinxTable.sendNotification'),
          align: 'left' as const,
          template: (row: any) => row.data.SendNotification,
        },
        {
          id: 'NotificationMethod',
          header: t('ELinxTable.notificationMethod'),
          align: 'left' as const,
          template: (row: any) => row.data.NotificationMethod,
        },
        {
          id: 'SendReminder',
          header: t('ELinxTable.sendReminder'),
          align: 'left' as const,
          template: (row: any) => row.data.SendReminder,
        },
        {
          id: 'ReminderFrequency',
          header: t('ELinxTable.reminderFrequency'),
          align: 'left' as const,
          template: (row: any) => row.data.ReminderFrequency,
        },
        {
          id: 'PaymentMethodDisplayOptions',
          header: t('ELinxTable.paymentMethodDisplayOptions'),
          align: 'left' as const,
          template: (row: any) => row.data.PaymentMethodDisplayOptions,
        },
        {
          id: 'IframeKey',
          header: t('ELinxTable.iframeKey'),
          align: 'left' as const,
          template: (row: any) => row.data.IframeKey,
        },
      ];

      // eFlex-specific columns
      const eFlexColumns =
        product === 'eFlex'
          ? [
              {
                id: 'FlexiblePaymentOptions',
                header: t('ELinxTable.flexiblePaymentOptions'),
                align: 'left' as const,
                template: (row: any) => row.data.FlexiblePaymentOptions,
              },
              {
                id: 'MinimumPaymentAccepted',
                header: t('ELinxTable.minimumPaymentAccepted'),
                align: 'left' as const,
                template: (row: any) => row.data.MinimumPaymentAccepted,
              },
              {
                id: 'RemainderPaymentDate',
                header: t('ELinxTable.remainderPaymentDate'),
                align: 'left' as const,
                template: (row: any) => row.data.RemainderPaymentDate,
              },
              {
                id: 'NumberOfInstallments',
                header: t('ELinxTable.numberOfInstallments'),
                align: 'left' as const,
                template: (row: any) => row.data.NumberOfInstallments,
              },
              {
                id: 'InstallmentFrequency',
                header: t('ELinxTable.installmentFrequency'),
                align: 'left' as const,
                template: (row: any) => row.data.InstallmentFrequency,
              },
            ]
          : [];

      const conditionalColumns = [];
      if (!clientAccountsEnabled && product !== 'ePay' && product !== 'eCollect' && product !== 'eFlex') {
        conditionalColumns.push(
          { id: 'Name', header: t('ELinxTable.name'), align: 'left' as const, template: (row: any) => row.data.Name },
          {
            id: 'Account',
            header: t('ELinxTable.account'),
            align: 'left' as const,
            template: (row: any) => row.data.Account,
          },
        );
      }

      return {
        id: 'bulkTransactionTable',
        version: '2' as const,
        zebra: true,
        columns: [
          baseColumns[0], // Id
          baseColumns[1], // ClientAccountID
          ...conditionalColumns, // Name and Account if enabled
          ...baseColumns.slice(2), // Rest of the base columns
          ...eFlexColumns, // eFlex-specific columns if product is eFlex
        ],
        rows:
          Object.values(transactionData).map((transaction, index) => ({
            id: `transaction-${index}`,
            data: {
              Id: String(index + 1),
              ClientAccountID: transaction.ClientAccountID ? String(transaction.ClientAccountID) : '',
              SenderName: transaction.SenderName ? String(transaction.SenderName) : '',
              ReceiverName: transaction.ReceiverName ? String(transaction.ReceiverName) : '',
              ReceiverEmailAddress: transaction.ReceiverEmailAddress ? String(transaction.ReceiverEmailAddress) : '',
              ReceiverPhoneNumber: transaction.ReceiverPhoneNumber ? String(transaction.ReceiverPhoneNumber) : '',
              PaymentMethod: transaction.PaymentMethod ? String(transaction.PaymentMethod) : '',
              BankingPaymentRail: transaction.BankingPaymentRail ? String(transaction.BankingPaymentRail) : '',
              Name: String(
                transaction.CompanyName
                  ? transaction.CompanyName
                  : transaction.RecipientName
                    ? transaction.RecipientName
                    : transaction.ReceiverName
                      ? transaction.ReceiverName
                      : `${String(transaction.FirstName)} ${String(transaction.LastName)}`,
              ),
              Account: (
                <>
                  {transaction.CardNumber ? transaction.CardNumber : ''}
                  {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
                  {transaction.ReceiverEmailAddress ? transaction.ReceiverEmailAddress : ''}
                  {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
                  {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
                  {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
                  {transaction.Token ? t('Table.Row.accountToken', { token: transaction.Token }) : ''}
                  {transaction.CreditCardToken
                    ? t('Table.Row.accountCreditCardToken', { token: transaction.CreditCardToken })
                    : ''}
                  {transaction.CardToken ? t('Table.Row.accountCardToken', { token: transaction.CardToken }) : ''}
                  {transaction.FlinksAccountID
                    ? t('Table.Row.accountFlinksAccountID', { token: transaction.FlinksAccountID })
                    : ''}
                  {transaction.FlinksLoginID
                    ? t('Table.Row.accountFlinksLoginID', { token: transaction.FlinksLoginID })
                    : ''}
                  {transaction.PlaidPublicToken
                    ? t('Table.Row.accountPlaidPublicToken', { token: transaction.PlaidPublicToken })
                    : ''}
                  {transaction.PlaidAccessToken
                    ? t('Table.Row.accountPlaidAccessToken', { token: transaction.PlaidAccessToken })
                    : ''}
                  {transaction.PlaidAccountID
                    ? t('Table.Row.accountPlaidAccountID', { token: transaction.PlaidAccountID })
                    : ''}
                  {transaction.PlaidProcessorToken
                    ? t('Table.Row.accountPlaidProcessorToken', { token: transaction.PlaidProcessorToken })
                    : ''}
                  {transaction.InveriteRequestGUID
                    ? t('Table.Row.accountInveriteRequestGUID', { token: transaction.InveriteRequestGUID })
                    : ''}
                </>
              ),
              Amount: `$ ${String(transaction.Amount || '')}`,
              Currency: transaction.Currency ? String(transaction.Currency) : '',
              Type: transaction.Frequency
                ? t('Table.Row.typeScheduled')
                : transaction.Amount
                  ? t('Table.Row.typeTransaction')
                  : t('Table.Row.typeBeneficiary'),
              PaymentType: transaction.PaymentType ? String(transaction.PaymentType) : '',
              MessageForRecipient: transaction.RecipientMessage ? String(transaction.RecipientMessage) : '',
              PaymentFrequency: transaction.Frequency ? String(transaction.Frequency) : '',
              NameOfFrequency: transaction.NameOfFrequency ? String(transaction.NameOfFrequency) : '',
              ScheduleStartDate: transaction.ScheduleStartDate ? String(transaction.ScheduleStartDate) : '',
              ScheduleEndDate: transaction.ScheduleEndDate ? String(transaction.ScheduleEndDate) : '',
              SemiMonthlyFrequencyType: transaction.SemiMonthlyFrequencyType
                ? String(transaction.SemiMonthlyFrequencyType)
                : '',
              EndingAfterPayments: transaction.EndingAfterPayments ? String(transaction.EndingAfterPayments) : '',
              InvoiceNumber: transaction.InvoiceNumber ? String(transaction.InvoiceNumber) : '',
              DocumentID: transaction.DocumentID ? String(transaction.DocumentID) : '',
              ClientReferenceNumber: transaction.ClientReferenceNumber ? String(transaction.ClientReferenceNumber) : '',
              TransactionLabel: transaction.TransactionLabel ? String(transaction.TransactionLabel) : '',
              Note: transaction.Note ? String(transaction.Note) : '',
              SendNotification: transaction.SendNotification ? String(transaction.SendNotification) : '',
              NotificationMethod: transaction.NotificationMethod ? String(transaction.NotificationMethod) : '',
              SendReminder: transaction.SendReminder ? String(transaction.SendReminder) : '',
              ReminderFrequency: transaction.ReminderFrequency ? String(transaction.ReminderFrequency) : '',
              PaymentMethodDisplayOptions: transaction.PaymentMethodDisplayOptions
                ? String(transaction.PaymentMethodDisplayOptions)
                : '',
              IframeKey: transaction.IframeKey ? String(transaction.IframeKey) : '',
              // eFlex-specific fields
              FlexiblePaymentOptions: transaction.FlexiblePaymentOptions
                ? String(transaction.FlexiblePaymentOptions)
                : '',
              MinimumPaymentAccepted: transaction.MinimumPaymentAccepted
                ? `$ ${String(transaction.MinimumPaymentAccepted)}`
                : '',
              RemainderPaymentDate: transaction.RemainderPaymentDate ? String(transaction.RemainderPaymentDate) : '',
              NumberOfInstallments: transaction.NumberOfInstallments ? String(transaction.NumberOfInstallments) : '',
              InstallmentFrequency: transaction.InstallmentFrequency ? String(transaction.InstallmentFrequency) : '',
            },
          })) || [],
      };
    } else if (product === 'Client Account Transfer') {
      return {
        id: 'bulkTransactionTable',
        version: '2' as const,
        zebra: true,
        columns: [
          {
            id: 'Id',
            header: t('ClientAccountTransferTable.id'),
            align: 'left' as const,
            template: (row: any) => row.data.Id,
          },
          {
            id: 'TransferType',
            header: t('ClientAccountTransferTable.transferType'),
            align: 'left' as const,
            template: (row: any) => row.data.TransferType,
          },
          {
            id: 'DebitorClientAccountID',
            header: t('ClientAccountTransferTable.debitorClientAccountID'),
            align: 'left' as const,
            template: (row: any) => row.data.DebitorClientAccountID,
          },
          {
            id: 'RecipientClientAccountID',
            header: t('ClientAccountTransferTable.recipientClientAccountID'),
            align: 'left' as const,
            template: (row: any) => row.data.RecipientClientAccountID,
          },
          {
            id: 'Amount',
            header: t('ClientAccountTransferTable.amount'),
            align: 'left' as const,
            template: (row: any) => row.data.Amount,
          },
          {
            id: 'FundingRail',
            header: t('ClientAccountTransferTable.fundingRail'),
            align: 'left' as const,
            template: (row: any) => row.data.FundingRail,
          },
          {
            id: 'WithdrawRail',
            header: t('ClientAccountTransferTable.withdrawRail'),
            align: 'left' as const,
            template: (row: any) => row.data.WithdrawRail,
          },
          {
            id: 'Currency',
            header: t('ClientAccountTransferTable.currency'),
            align: 'left' as const,
            template: (row: any) => row.data.Currency,
          },
          {
            id: 'ReverseOnFailure',
            header: t('ClientAccountTransferTable.reverseOnFailure'),
            align: 'left' as const,
            template: (row: any) => row.data.ReverseOnFailure,
          },
          {
            id: 'ClientReferenceNumber',
            header: t('ClientAccountTransferTable.clientReferenceNumber'),
            align: 'left' as const,
            template: (row: any) => row.data.ClientReferenceNumber,
          },
          {
            id: 'Notes',
            header: t('ClientAccountTransferTable.notes'),
            align: 'left' as const,
            template: (row: any) => row.data.Notes,
          },
          {
            id: 'TransactionLabel',
            header: t('ClientAccountTransferTable.transactionLabel'),
            align: 'left' as const,
            template: (row: any) => row.data.TransactionLabel,
          },
        ],
        rows:
          Object.values(transactionData).map((transaction, index) => ({
            id: `transfer-${index}`,
            data: {
              Id: String(index + 1),
              TransferType: transaction.TransferType ? String(transaction.TransferType) : '',
              DebitorClientAccountID: transaction.DebitorClientAccountID
                ? String(transaction.DebitorClientAccountID)
                : '',
              RecipientClientAccountID: transaction.RecipientClientAccountID
                ? String(transaction.RecipientClientAccountID)
                : '',
              Amount: `$ ${String(transaction.Amount || '')}`,
              FundingRail: transaction.FundingRail ? String(transaction.FundingRail) : '',
              WithdrawRail: transaction.WithdrawRail ? String(transaction.WithdrawRail) : '',
              Currency: transaction.Currency ? String(transaction.Currency) : '',
              ReverseOnFailure: transaction.ReverseOnFailure !== undefined ? String(transaction.ReverseOnFailure) : '',
              ClientReferenceNumber: transaction.ClientReferenceNumber ? String(transaction.ClientReferenceNumber) : '',
              Notes: transaction.Notes ? String(transaction.Notes) : '',
              TransactionLabel: transaction.TransactionLabel ? String(transaction.TransactionLabel) : '',
            },
          })) || [],
      };
    }
    const defaultColumns = [
      { id: 'Id', header: t('Table.id'), align: 'left' as const, template: (row: any) => row.data.Id },
      { id: 'Amount', header: t('Table.amount'), align: 'left' as const, template: (row: any) => row.data.Amount },
      { id: 'Service', header: t('Table.service'), align: 'left' as const, template: (row: any) => row.data.Service },
    ];

    const conditionalDefaultColumns = [];
    if (clientAccountsEnabled) {
      conditionalDefaultColumns.push({
        id: 'ClientAccountID',
        header: t('Table.clientAccountID'),
        align: 'left' as const,
        template: (row: any) => row.data.ClientAccountID,
      });
    }
    if (!clientAccountsEnabled) {
      conditionalDefaultColumns.push(
        { id: 'Name', header: t('Table.name'), align: 'left' as const, template: (row: any) => row.data.Name },
        { id: 'Account', header: t('Table.account'), align: 'left' as const, template: (row: any) => row.data.Account },
      );
    }

    return {
      id: 'bulkTransactionTable',
      version: '2' as const,
      zebra: true,
      columns: [
        defaultColumns[0], // Id
        ...conditionalDefaultColumns, // ClientAccountID, Name, Account based on conditions
        ...defaultColumns.slice(1), // Amount, Service
      ],
      rows:
        Object.values(transactionData).map((transaction, index) => ({
          id: `default-${index}`,
          data: {
            Id: String(index + 1),
            ClientAccountID: transaction.ClientAccountID ? String(transaction.ClientAccountID) : '',
            Name: String(
              transaction.CompanyName
                ? transaction.CompanyName
                : transaction.RecipientName
                  ? transaction.RecipientName
                  : transaction.ReceiverName
                    ? transaction.ReceiverName
                    : `${String(transaction.FirstName)} ${String(transaction.LastName)}`,
            ),
            Account: (
              <>
                {transaction.CardNumber ? transaction.CardNumber : ''}
                {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
                {transaction.ReceiverEmailAddress ? transaction.ReceiverEmailAddress : ''}
                {transaction.Email ? transaction.Email : ''}
                {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
                {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
                {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
                {transaction.Token ? t('Table.Row.accountToken', { token: transaction.Token }) : ''}
                {transaction.CreditCardToken
                  ? t('Table.Row.accountCreditCardToken', { token: transaction.CreditCardToken })
                  : ''}
                {transaction.CardToken ? t('Table.Row.accountCardToken', { token: transaction.CardToken }) : ''}
                {transaction.FlinksAccountID
                  ? t('Table.Row.accountFlinksAccountID', { token: transaction.FlinksAccountID })
                  : ''}
                {transaction.FlinksLoginID
                  ? t('Table.Row.accountFlinksLoginID', { token: transaction.FlinksLoginID })
                  : ''}
                {transaction.PlaidPublicToken
                  ? t('Table.Row.accountPlaidPublicToken', { token: transaction.PlaidPublicToken })
                  : ''}
                {transaction.PlaidAccessToken
                  ? t('Table.Row.accountPlaidAccessToken', { token: transaction.PlaidAccessToken })
                  : ''}
                {transaction.PlaidAccountID
                  ? t('Table.Row.accountPlaidAccountID', { token: transaction.PlaidAccountID })
                  : ''}
                {transaction.PlaidProcessorToken
                  ? t('Table.Row.accountPlaidProcessorToken', { token: transaction.PlaidProcessorToken })
                  : ''}
                {transaction.InveriteRequestGUID
                  ? t('Table.Row.accountInveriteRequestGUID', { token: transaction.InveriteRequestGUID })
                  : ''}
              </>
            ),
            Amount: `$${formattedCurrency((transaction.Amount || '').toString() || '')}`,
            Service: getServiceName(product, service),
          },
        })) || [],
    };
  };

  const TableProps = useMemo(() => {
    return getTableProps() as any;
  }, [transactionData]);

  return (
    <div>
      <Box flx="col" pb="lg" mb="md" gap="sm" jc="sb" width="100%" wrap="wrap">
        <FileUploadHeader
          product={product}
          service={service}
          fileName={fileName}
          numberOfTransactions={numberOfTransactions}
          amount={amount}
          currency={currency}
        />
        <Card style={{ padding: '16px' }}>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Table {...TableProps} />
          </div>
        </Card>
      </Box>
    </div>
  );
};

export default FileUploadReview;
